import { ZodSchema } from "@validation/utils/schemaUtils";
import { CraneType, LocationType, LocationUserType, MooringSide } from "__generated__/enums";
import { intl } from "LocaleWrapper";
import { openingHoursSchema } from "src/zod/custom.schema";
import z from "zod";

export const locationSelectOptionSchema = z.object({
	id: z.number().gt(0),
	description: z.string().min(1),
	type: z.enum(LocationType).nullish(),
	locationGroups: z.array(z.object({
		id: z.number().gt(0),
		description: z.string().min(1),
		type: z.enum(LocationType),
	})).nullish(),
});

export const locationSchema = z.object({
	id: z.number().nullable(),
	description: z.string().min(1).max(255),
	type: z.enum(LocationType),
	locationGroups: z.array(locationSelectOptionSchema).nullable(),
	externalId: z.string().max(255),
	code: z.string().max(255),
	abbreviation: z.string().max(255),
	email: z.email().or(z.literal("")).optional(),
	telephone: z.string().max(255),
	preHandlingTimeMin: z.number().max(9999).nullable(),
	postHandlingTimeMin: z.number().max(9999).nullable(),
	loadingDepth: z.number().max(9999).nullable(),
	maxShipLength: z.number().nullable(),
	maxShipWidth: z.number().nullable(),
	maxShipDraught: z.number().nullable(),
	notes: z.string(),
	color: z.string(),
	isActive: z.boolean(),
	isErpLocation: z.boolean(),
	uabRegistration: z.boolean(),

	address: z.object({
		name1: z.string().max(100),
		name2: z.string().max(100),
		street: z.string().max(200),
		zipCode: z.string().max(20),
		town: z.string().max(100),
		country: z.string(),
	}),

	contacts: z.array(z.object({
		id: z.number().nullable(),
		name: z.string().max(50),
		insertion: z.string().max(10),
		surName: z.string().max(50),
		email: z.email().or(z.literal("")).optional(),
		telephone: z.string().max(25),
		fax: z.string().max(25),
		visibleForShip: z.boolean(),
	})),

	encodedPolyline: z.string(),
	position: z.object({
		latitude: z.number().nullable(),
		longitude: z.number().nullable(),
	}),

	openingHours: openingHoursSchema,

	relationUsers: z.array(z.object({
		id: z.object({
			location: z.number().nullable(),
			customer: z.number().nullable(),
		}).nullable(),
		customer: ZodSchema.requiredField(z.object({
			id: z.number(),
			name: z.string(),
		}).nullable()),
		type: z.enum(LocationUserType),
	})),

	quays: z.array(z.object({
		id: z.number().nullable(),
		name1: z.string().min(1).max(255),
		length: z.number().max(5000).nullable(),
		depth: z.number().max(9999999.999).nullable(),
		latitude: z.number().nullable(),
		longitude: z.number().nullable(),
		loadingSpeedTon: z.number().max(65535).nullable(),
		loadingSpeedTeu: z.number().max(9999999.999).nullable(),
		loadingSpeedM3: z.number().max(9999999.999).nullable(),
		loadingSpeedMove: z.number().max(65535).nullable(),
		dischargingSpeedTon: z.number().max(65535).nullable(),
		dischargingSpeedTeu: z.number().max(9999999.999).nullable(),
		dischargingSpeedM3: z.number().max(9999999.999).nullable(),
		dischargingSpeedMove: z.number().max(65535).nullable(),

		bolders: z.array(z.object({
			id: z.number().nullable(),
			description: z.string().min(1).max(20),
			latitude: z.number().nullable(),
			longitude: z.number().nullable(),
			metersToNextBolder: z.number().nullable(),
			phantomBolder: z.boolean(),
			mooringSide: z.enum(MooringSide),
		})),

		cranes: z.array(z.object({
			id: z.number().nullable(),
			name: z.string().min(3).max(255),
			sequenceNumber: z.number().nullable(),
			craneType: z.enum(CraneType),
			fromBolder: ZodSchema.requiredField(z.object({
				id: z.number(),
				description: z.string(),
			}).nullable()),
			toBolder: ZodSchema.requiredField(z.object({
				id: z.number(),
				description: z.string(),
			}).nullable()),

			averageCraneSpeedInMovesPerHour: z.number().nullable(),
			maxHeightInMeters: z.number().nullable(),
			maxWidthInMeters: z.number().nullable(),
			outreachInContainers: z.number().nullable(),
			liftCapacityInTons: z.number().nullable(),

			twinningAllowed: z.boolean(),
			fullContainersAllowed: z.boolean(),
			canHandleOOG: z.boolean(),
			canHandleBundles: z.boolean(),

			handlingRates: z.array(z.object({
				id: z.number().nullable(),
				product: ZodSchema.requiredField(z.object({
					id: z.number(),
					name: z.string(),
				}).nullable()),
				handlingRateInTonsPerHour: z.number().nullable(),
			})),
		})),
	})),
}).superRefine((data, ctx) => {
	if (data.type === LocationType.Buoy && (data.locationGroups == null || data.locationGroups?.length == 0)) {
		ctx.addIssue({
			code: "custom",
			message: intl.formatMessage({ id: "ZOD_VALIDATION_REQUIRED" }),
			path: [ "locationGroups" ],
		});
	}
});

export type LocationSchemaInput = z.input<typeof locationSchema>;
export type LocationSchemaOutput = z.output<typeof locationSchema>;

